import { useActiveAgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/useActiveAgenticLogicView";
import { useDispatch, useSelector } from "@src/store";
import {
  getAiChatProps,
  getAtlasBuildingRuleId,
  getIsAtlasCreationSessionActive,
  setAiChat,
} from "@src/store/slices/global";

import {
  type AtlasRailCollapse,
  resolveAtlasRailCollapse,
  storeAtlasRailCollapsed,
} from "@src/features/ai/utils/atlasRailCollapse";
import { useAtlasChatLayout } from "./useAtlasChatLayout";

/**
 * Whether the Atlas rail is hidden on what is on screen now, and the two
 * actions that change it. Read by the three frames involved — the layout shell
 * (hides the rail's column), the rail header (the minus) and the page's top
 * bar (the Atlas button) — each outside the others' trees, so the surface
 * comes from the registered descriptor.
 */
export const useAtlasRailCollapse = (): AtlasRailCollapse & {
  hide: () => void;
  show: () => void;
} => {
  const dispatch = useDispatch();
  const { surface, view } = useActiveAgenticLogicView();
  const layout = useAtlasChatLayout();
  const { railCollapsed, ruleEditing } = useSelector(getAiChatProps);
  const sessionActive = useSelector(getIsAtlasCreationSessionActive);
  const buildingRuleId = useSelector(getAtlasBuildingRuleId);

  const setCollapsed = (collapsed: boolean) => {
    storeAtlasRailCollapsed(collapsed);
    dispatch(setAiChat({ key: "railCollapsed", value: collapsed }));
  };

  return {
    ...resolveAtlasRailCollapse({
      surfaceKind: surface?.kind ?? null,
      view,
      layout,
      sessionActive,
      buildingRuleId,
      ruleEditing,
      collapsed: railCollapsed,
    }),
    hide: () => setCollapsed(true),
    show: () => setCollapsed(false),
  };
};
